import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Blueshift } from '../multi-select.component';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore)

@Component({
  selector: 'app-month-view',
  templateUrl: './month-view.component.html',
  styleUrls: ['./month-view.component.scss'],
})
export class MonthViewComponent implements OnInit {

  @Input() month: dayjs.Dayjs;
  @Input() blueshifts: Blueshift[];
  @Output() blueshiftClickEvent = new EventEmitter<object>();

  daysOfWeekInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  weeks: Day[][];

  constructor() { }

  ngOnInit() {
    this.loadData();
  }

  /**
   * Gets the long name of a month appended with the year
   */
   getMonthTitle() {
    return this.month.format('MMMM YYYY');
  }

  /**
   * Passes through the Blueshift click event coming from the BlueshiftViewComponent
   * 
   * @param event 
   */
  emitBlueshiftClickEvent(event) {
    this.blueshiftClickEvent.emit(event);
  }

  private loadData() {
    if (this.getIsCurrentMonth(this.month)) {
      const sundayInCurrentWeek = dayjs().subtract(dayjs().day(), 'day');
      const startDate = this.getIsCurrentMonth(sundayInCurrentWeek) ? sundayInCurrentWeek : this.month.date(1);
      this.weeks = this.getWeeks(startDate);
    } else {
      this.weeks = this.getWeeks(this.month.date(1));
    }
  }

  /**
   * Given the start date to initialize the calendar with, determine each week's data
   * up until the last day of the month
   * 
   * @param startDate day to start the calendar labels
   * @returns Day[][] an array representing the weeks of the month, where each week
   * is itself an array with each day of the week
   */
  private getWeeks(startDate: dayjs.Dayjs): Day[][] {
    let weeks: Day[][] = [];
    let currWeek: Day[] = [];
    let currDate = dayjs(startDate);

    // initialize first week with empty vals up to startDate day of week index
    for (let i = 0; i < currDate.day(); i++) {
      currWeek.push(
        {
          date: undefined,
          label: undefined,
          shifts: undefined,
          isToday: undefined
        }
      );
    }

    // initialize the rest of the month, up to the last day of the month
    while (currDate.isSameOrBefore(startDate.endOf('month'), 'day')) {
      const data: Day = this.getDataForDate(currDate);
      const currDay = currDate.day();
      if (currDay === 0) {
        currWeek = [data];
      } else {
        currWeek.push(data);
      }
      if (currDay === 6) {
        weeks.push(currWeek);
      }
      currDate = currDate.add(1, 'day');
    }

    // fill in empty days for last week of month and add the last week to the list, 
    // if the last week of the month is not a full week
    if (currDate.day() !== 0) {
      for (let i = currDate.day(); i < 7; i++) {
        currWeek.push(
          {
            date: undefined,
            label: undefined,
            shifts: undefined,
            isToday: undefined
          }
        );
      }
      weeks.push(currWeek);
    }
    return weeks;
  }

  /**
   * Returns the Day object for the date provided
   * 
   * @param date to return data for
   * @returns Day object with date object, label for calendar, Blueshifts, and indicator whether the day is today
   */
  private getDataForDate(date: dayjs.Dayjs): Day {
    return {
      date: date,
      label: date.date(),
      shifts: this.getShiftsForDate(date),
      isToday: this.getIsToday(date)
    };
  }

  /**
   * Helper function to check if the month passed is the current month (local time)
   * 
   * @param date dayjs.Dayjs object with month to check
   * @returns true if the month is current month, false if not
   */
  private getIsCurrentMonth(date: dayjs.Dayjs): boolean {
    return date.isSame(dayjs(), 'month');
  }

  /**
   * Helper function to check if the date passed is today's date (local time)
   * 
   * @param date dayjs.Dayjs object with date to check
   * @returns true if the date is today, false if not
   */
  private getIsToday(date: dayjs.Dayjs): boolean {
    return date.isSame(dayjs(), 'day');
  }

  /**
   * Helper function to retrieve all Blueshifts for a given date
   * 
   * @param date date to retrieve Blueshifts for
   * @returns an array of Blueshifts
   */
  private getShiftsForDate(date: dayjs.Dayjs): Blueshift[] {
    return this.blueshifts.filter(blueshift => dayjs(blueshift.startDateTime).isSame(date, 'day'));
  }
}

export class Day {
  date: dayjs.Dayjs;
  label: number;
  shifts: Blueshift[];
  isToday: boolean;
}
